/**
 * SIL-1346 - THE Active Verification explainer copy. Spec:
 * docs/superpowers/specs/2026-09-28-exchange-buyer-ui-design.md §4.4; wording
 * verbatim from docs/exchange/design-handoff/README.md §4, which is itself
 * grounded in `resources/plans/wayfinder-active-verification-spec.md` §7/§9.
 *
 * Moved here from `apps/customer/src/components/exchange/copy/
 * activeVerification.ts` (SIL-1383) so `apps/customer` and `apps/exchange`
 * share exactly one copy of this text instead of each carrying its own —
 * the same reason the design tokens moved to
 * `packages/shared/src/styles/tokens.css` in SIL-1380. Hand-copying is how
 * this text (already product- and legal-reviewed) drifts.
 *
 * This module is the ONLY place any of these sentences exist. The explainer
 * modal, the listing detail's "What is tested" link, the node Passport drawer,
 * My Assets, and the public exchange site all import from here. A component
 * that inlines one of these sentences is the bug this module exists to
 * prevent: a second copy is one that will not be updated when the first is.
 * `apps/customer/src/components/exchange/__tests__/activeVerificationCopy.test.ts`
 * greps apps/customer for the intro and the not-covered sentences and fails
 * if either appears outside `apps/customer/src/components/exchange/copy/
 * activeVerification.ts` — which now re-exports from this module rather than
 * defining the strings itself, so that guard still holds.
 *
 * Plain strings only - no JSX, no markdown. The handoff bolds "Verify this
 * asset" inside the intro; that is a rendering choice, so it is not encoded
 * here as markup a renderer would have to parse (and a stray `**` is exactly
 * the kind of artifact that leaks into a screenshot).
 *
 * The 90-day validity comes from `VERIFICATION_DISPLAY_TTL_DAYS`
 * (this package's verification presentation module) rather than a
 * hardcoded "90" - the TTL is a product rule enforced in that module, and the
 * explainer promising a different number than the badge enforces would be a
 * straight contradiction on the same page.
 */
import { VERIFICATION_DISPLAY_TTL_DAYS } from '../verification/presentation.js';

export const ACTIVE_VERIFICATION_TITLE = 'About Active Verification';

/** Copy for the affordances that open the explainer. */
export const ACTIVE_VERIFICATION_TRIGGER_LABEL = 'What is tested';
/** The longer trigger used in prose (My Assets description, spec §4.8). */
export const ACTIVE_VERIFICATION_HOW_IT_WORKS_LABEL = 'How Active Verification works';
/** The modal's single footer button. There is no Cancel (handoff §4). */
export const ACTIVE_VERIFICATION_DONE_LABEL = 'Done';

export const ACTIVE_VERIFICATION_INTRO =
  'Active Verification is a sustained GPU workload run on the asset itself, started by the seller with Verify this asset. ' +
  "Results are written to each node's Silicon Passport and summarized on the listing. " +
  `Every run is dated and remains valid for ${VERIFICATION_DISPLAY_TTL_DAYS} days.`;

export const ACTIVE_VERIFICATION_CHECKS_HEADING = 'What is measured';

/** Column headers for the checks table, in order. */
export const ACTIVE_VERIFICATION_CHECK_COLUMNS = ['Check', 'What it measures', 'Pass condition'] as const;

export interface ActiveVerificationCheck {
  /** Stable key for React lists and for a drawer row that maps a result onto a check. */
  id: 'sustained_compute' | 'memory_bandwidth' | 'latency_stability' | 'device_stability' | 'thermal_power' | 'serial_match';
  name: string;
  measures: string;
  passCondition: string;
}

/**
 * The six checks, in the order the handoff lists them - the drawer and the
 * listing's Active Verification panel render them in this order too, so a
 * buyer comparing the explainer against a run reads the same sequence twice.
 */
export const ACTIVE_VERIFICATION_CHECKS: readonly ActiveVerificationCheck[] = [
  {
    id: 'sustained_compute',
    name: 'Sustained compute',
    measures: 'Matrix-multiply load held for at least 4 minutes',
    passCondition: 'Throughput at least 50% of the SKU roofline; drift under 15%',
  },
  {
    id: 'memory_bandwidth',
    name: 'Memory bandwidth fingerprint',
    measures: "Streaming bandwidth vs. the claimed SKU's spec",
    passCondition: '50-105% of spec; outside this range flags an identity mismatch',
  },
  {
    id: 'latency_stability',
    name: 'Latency stability',
    measures: 'Variation in per-pass latency',
    passCondition: 'Coefficient of variation under 8%',
  },
  {
    id: 'device_stability',
    name: 'Device stability',
    measures: 'GPU resets or device-lost events',
    passCondition: 'None',
  },
  {
    id: 'thermal_power',
    name: 'Thermal & power',
    measures: 'Temperature, power and throttle state from the BMC (Tier 1-2)',
    passCondition: 'No thermal throttle or power-cap transition',
  },
  {
    id: 'serial_match',
    name: 'Serial match',
    measures: 'BMC-attested GPU serials against the Passport (Tier 1)',
    passCondition: 'Every serial matches',
  },
] as const;

export const ACTIVE_VERIFICATION_VERDICTS_HEADING = 'Verdicts';

export interface ActiveVerificationVerdictCopy {
  /** Matches the verdict families in this package's presentation module. */
  id: 'pass' | 'pass_with_observations' | 'fail';
  label: string;
  /** Semantic tone, not a colour - the renderer owns the palette. */
  tone: 'success' | 'warn' | 'error';
  /** Absent for a plain pass: the label says all there is to say. */
  detail?: string;
}

export const ACTIVE_VERIFICATION_VERDICTS: readonly ActiveVerificationVerdictCopy[] = [
  { id: 'pass', label: 'Pass', tone: 'success' },
  {
    id: 'pass_with_observations',
    label: 'Pass with observations',
    tone: 'warn',
    detail:
      'No failures, with a signal worth disclosing, such as thermal drift the controller did not confirm.',
  },
  {
    id: 'fail',
    label: 'Fail',
    tone: 'error',
    detail:
      'Thermal throttle, power-cap transition, low baseline, high jitter or device lost. Failed runs stay on the Passport, and the worst run in the window is the one shown.',
  },
] as const;

export const ACTIVE_VERIFICATION_TIERS_HEADING = 'Evidence tiers';

export interface ActiveVerificationTierCopy {
  tier: 1 | 2 | 3;
  label: string;
  /** Only Tier 3 carries one, and it is load-bearing, not decoration. */
  note?: string;
}

/**
 * Tier 3 never renders the Verified mark (verification spec §9). The badge-level
 * string for a Tier 3 result is `TIER_3_SELF_REPORTED_LABEL` in this
 * package's presentation module - a hard-required verbatim label owned
 * there, not restated here; this table is the explainer's three-row summary
 * of the tiers, not a second source for that badge.
 */
export const ACTIVE_VERIFICATION_TIERS: readonly ActiveVerificationTierCopy[] = [
  { tier: 1, label: 'Controller-attested' },
  { tier: 2, label: 'Controller-polled' },
  { tier: 3, label: 'Seller self-reported', note: 'Shown without the Verified mark.' },
] as const;

export const ACTIVE_VERIFICATION_NOT_COVERED_HEADING = 'What a pass does not cover';

export const ACTIVE_VERIFICATION_NOT_COVERED =
  'Active Verification tests the GPUs. It does not establish the condition of the CPU, system memory, ' +
  'motherboard, network interfaces, power supplies, firmware or warranty, and it does not predict ' +
  'remaining useful life. Data sanitization and chain of custody are separate records on the Passport.';
